import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { addUser } from '@modules/teams/services';
import { AddUserMutationVariables, UserToAdd } from './AddTeamMemberScreen.types';
import { AddUserPayload } from '@modules/teams/types';
import { Box, Button, Card, Page } from '@base-app';
import { LeavingPageModal } from '@builder';
import { OnSaveFormValues } from '@modules/teams/components/AddTeamMemberForm/AddTeamMemberForm.types';
import { PATHS } from '@constants/routes';
import { Sizes } from '@common/types';
import { TEAMS_TRANSLATION } from '@modules/teams/constants';

import AddTeamMemberCard from '@modules/teams/components/AddTeamMemberCard/AddTeamMemberCard';
import AddTeamMemberRow from '@modules/teams/components/AddTeamMemberRow/AddTeamMemberRow';
import useMutationTeams from '@modules/teams/hooks/useMutationTeams';
import { COMMON_TRANSLATION } from '@/common/constants';
import { useBlocker } from 'react-router-dom';

const AddTeamMemberScreen: FC = () => {
  const { t } = useTranslation(['common', 'teams']);
  const [usersToAdd, setUsersToAdd] = useState<UserToAdd[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [usersInEditMode, setUsersInEditMode] = useState<number[]>([]);
  const [areInvitesSent, setAreInvitesSent] = useState<boolean>(false);
  const blocker = useBlocker(usersToAdd.length > 0 && !areInvitesSent);
  const isBlocked = blocker.state === 'blocked';

  useEffect(() => {
    if (isBlocked && usersToAdd.length === 0 && areInvitesSent) {
      blocker.reset?.();
    }
  }, [blocker, usersToAdd, areInvitesSent]);

  const handleAddTeamMember = (values: OnSaveFormValues): void => {
    const users = [...usersToAdd, values];
    setUsersToAdd(users);
  };

  const handleTeamMemberSave = (userData: OnSaveFormValues, index: number): void => {
    usersToAdd[index] = userData;
    setUsersToAdd([...usersToAdd]);
  };

  const handleTeamMemberDelete = (index: number): void => {
    const users = usersToAdd.filter((user, i) => i !== index);
    setUsersToAdd([...users]);
  };

  const handleAddAnotherMembers = (): void => {
    setUsersToAdd([]);
    setAreInvitesSent(false);
    setErrors([]);
  };

  const { mutateAsync } = useMutationTeams<any, any, AddUserMutationVariables>({
    mutationFn: async (addUserPayloadVariables: AddUserMutationVariables) => {
      const { email, firstName, groupId, lastName, roleId } = addUserPayloadVariables;
      const addUserPayload: AddUserPayload = {
        email,
        firstName,
        groupId,
        lastName,
        roleId,
      };

      return await addUser(addUserPayload);
    },
    onSuccess: (response: any, { index }: AddUserMutationVariables) => {
      // filter out users successfully saved.
      const email: string = response?.profile?.email;

      setUsersToAdd((prevUsersToAdd) => {
        prevUsersToAdd[index] = {
          ...prevUsersToAdd[index],
          sentInviteResponse: t('add_team_member_success', { email, ns: TEAMS_TRANSLATION }),
          sentInviteError: false,
        };
        return [...prevUsersToAdd];
      });
    },
    onError: (error: any, { index }: AddUserMutationVariables) => {
      const data: any = JSON.parse(error.config.data);
      const email: string = data.profile.email;
      const msg: string =
        error?.response?.data?.errorCauses !== undefined && error?.response?.data?.errorCauses.length > 0
          ? error?.response?.data?.errorCauses[0].errorSummary
          : t('add_team_member_error_generic', { email, ns: TEAMS_TRANSLATION });

      setUsersToAdd((prevUsersToAdd) => {
        prevUsersToAdd[index] = {
          ...prevUsersToAdd[index],
          sentInviteResponse: msg,
          sentInviteError: true,
        };

        return [...prevUsersToAdd];
      });

      setErrors((prevVal) => {
        return [...prevVal, msg];
      });
    },
  });

  const handleTeamMembersSave = async (): Promise<void> => {
    setErrors([]);
    setIsSaving(true);
    setAreInvitesSent(false);

    const promises: any = [];

    usersToAdd.forEach((user, index) => {
      const addUserPayload: AddUserMutationVariables = {
        ...user,
        roleId: user.role.id,
        groupId: user.role.group?.id,
        index,
      };

      promises.push(mutateAsync(addUserPayload));
    });

    await Promise.all(promises)
      .finally(() => {
        setIsSaving(false);
        setAreInvitesSent(true);
      })
      .catch(() => {});
  };

  const handleEditMember = (isEditMode: boolean, index: number): void => {
    const userIndex = usersInEditMode.findIndex((i) => i === index);

    if (isEditMode && userIndex === -1) {
      setUsersInEditMode([...usersInEditMode, index]);
    } else if (!isEditMode && userIndex !== -1) {
      usersInEditMode.splice(userIndex, 1);
      setUsersInEditMode([...usersInEditMode]);
    }
  };

  return (
    <Page
      backTitle={t('team_management', { ns: TEAMS_TRANSLATION })}
      backUrl={`/${PATHS.TEAMS}`}
      title={t('add_team_members', { ns: TEAMS_TRANSLATION })}
    >
      <Card css={{ padding: '$8' }}>
        <AddTeamMemberCard
          showForm={!areInvitesSent && !isSaving}
          showSendingMessage={!areInvitesSent && isSaving}
          showSuccessMessage={areInvitesSent && !isSaving && errors.length !== usersToAdd.length}
          showErrorMessage={areInvitesSent && !isSaving && areInvitesSent && errors.length === usersToAdd.length}
          onAddTeamMember={handleAddTeamMember}
        />
      </Card>

      {areInvitesSent && errors.length > 0 && errors.length !== usersToAdd.length && (
        <Box css={{ marginTop: '$8', color: '$red', fontWeight: '$bold' }}>
          {t('add_team_member_error_note', { ns: TEAMS_TRANSLATION })}
        </Box>
      )}
      <Box css={{ marginTop: '$8' }}>
        {usersToAdd.map((user: UserToAdd, index) => (
          <AddTeamMemberRow
            user={user}
            key={index}
            sentInviteError={user.sentInviteError}
            sentInviteResponse={user.sentInviteResponse}
            disabled={isSaving}
            onEdit={(isEdit) => handleEditMember(isEdit, index)}
            onSave={(userData) => handleTeamMemberSave(userData, index)}
            onDelete={() => handleTeamMemberDelete(index)}
          />
        ))}
      </Box>
      {!areInvitesSent && (
        <Button
          css={{ margin: 'auto', mt: '$10' }}
          data-testid="add-team-member-screen-button-send"
          disabled={usersToAdd.length < 1 || isSaving || usersInEditMode.length > 0}
          size={Sizes.LARGE}
          onClick={handleTeamMembersSave}
        >
          {t('save_and_send_invites', { ns: TEAMS_TRANSLATION })}
        </Button>
      )}
      {areInvitesSent && (
        <Button
          css={{ margin: 'auto', mt: '$10' }}
          data-testid="add-team-member-screen-button-add"
          size={Sizes.LARGE}
          onClick={handleAddAnotherMembers}
        >
          {t('add_another_team_members', { ns: TEAMS_TRANSLATION })}
        </Button>
      )}
      {isBlocked && (
        <LeavingPageModal
          confirmText={t('yes_exit', { ns: COMMON_TRANSLATION })}
          declineText={t('modal_cancel', { ns: COMMON_TRANSLATION })}
          description={t('team_member_added_modal_description', { ns: TEAMS_TRANSLATION })}
          isShowing={isBlocked}
          onCancel={() => blocker.reset()}
          onConfirm={() => blocker.proceed()}
          title={t('team_member_added_modal_title', { ns: TEAMS_TRANSLATION })}
        />
      )}
    </Page>
  );
};

export default AddTeamMemberScreen;
